import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.mfa_required) return data; // caller completes the second factor
    setUser(data);
    return data;
  };

  const verifyMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token, code });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  const isSuper = !!user && (user.is_super || user.role === "director" || user.role === "admin");
  const can = (module, action) => {
    if (!user) return false;
    if (isSuper) return true;
    return ((user.permissions || {})[module] || []).includes(action);
  };
  const hasFlag = (flag) => {
    if (!user) return false;
    if (isSuper) return true;
    return !!(user.flags || {})[flag];
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, verifyMfa, logout, checkAuth, isSuper, can, hasFlag }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
